import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { showLocalizedToast } from '@/components/localizedToast';
import type { AttributeType } from '@/api/models';
import {
    useAllBrandsQuery,
    useAttributeTypesQuery,
    useCategoryPickerQuery,
    useSupplierPickerQuery,
} from '@/api/queries';
import {
    categoryOptionsOf,
    namedOptionsOf,
    type NameOption,
} from '@/pages/catalogImport/names';
import {
    groupFieldsSuggestedRow,
    groupSiblings,
    useCatalogImport,
    type VariantMismatchField,
} from '@/pages/catalogImport/store';
import {
    notesOf,
    statusOf,
    type ImportRowExclusionHandlers,
    type ImportRowNote,
    type ImportRowStatus,
} from '../../importRows';
import { readDrawerRow, writeDrawerRow } from './rowDrawerAddress';
import {
    axisOptionsOf,
    categoryAxesOf,
    fieldErrorsOf,
    formOf,
    hasUnsavedEdits,
    isEmptyPatch,
    newValueTextsOf,
    NO_EDITS,
    patchesOf,
    pickerOptionsOf,
    type RowDrawerAxis,
    type RowDrawerContext,
    type RowDrawerEdits,
    type RowDrawerField,
    type RowDrawerFieldErrors,
    type RowDrawerForm,
} from './rowDrawerForm';
import {
    barcodeFixesOf,
    barcodesOf,
    candidatesOf,
    FORM_FIELD_OF,
    groupMismatchOf,
    pickedVariantIdOf,
    variantFixOf,
    type RowDrawerBarcodeFixCode,
    type RowDrawerBarcodeFixModel,
    type RowDrawerBarcodesModel,
    type RowDrawerCandidate,
    type RowDrawerGroupMismatch,
    type RowDrawerPickedVariant,
    type RowDrawerVariantFixModel,
    type RowDrawerVariantPicks,
} from './rowDrawerResolutions';

export type UseRowDrawerOptions = ImportRowExclusionHandlers & {
    /**
     * Checks every row again once the store holds the saved edits: the
     * step's `onRequestRecheck`, which also waits for a running check.
     */
    onRecheck: () => void;
    /**
     * The group pick was applied (a bulk action the store snapshots): the
     * step's toast of its count with Undo (`useBulkFixes`).
     */
    onGroupFixApplied: (changed: number) => void;
};

/** Whether the pickers have the catalog: its lists and the attribute dictionary. */
export type RowDrawerCatalogStatus = 'loading' | 'ready' | 'error';

/** The row the drawer shows, with the form over it. */
export type RowDrawerModel = {
    rowNumber: number;
    status: ImportRowStatus;
    isExcluded: boolean;
    sku: string;
    /** Every row of the row's product (this one included); one row when it has no group. */
    groupRows: number[];
    /** Every problem, then every warning, of the last check. */
    notes: ImportRowNote[];
    /**
     * The catalog variants the row could mean (`BARCODE_AMBIGUOUS`,
     * `SKU_BARCODE_CONFLICT`), to pick from; none once one is picked.
     */
    candidates: RowDrawerCandidate[];
    /** The variant the user picked: the row restocks it. */
    pick: RowDrawerPickedVariant | null;
    /** `GROUP_FIELDS_DIFFER`: every row of the group with its values for the fields that differ. */
    groupMismatch: RowDrawerGroupMismatch | null;
    /** `VARIANT_FIELDS_DIFFER`: each differing figure's values with their rows, to pick one. */
    variantFix: RowDrawerVariantFixModel | null;
    /** `BARCODES_KEPT_AS_ALTERNATES`: the variant's main and extra barcodes. */
    barcodes: RowDrawerBarcodesModel | null;
    /** `BARCODE_LOOKS_LIKE_IMEI`, `BARCODE_SHARED_ACROSS_SKUS`: the fix for the row's group. */
    barcodeFixes: RowDrawerBarcodeFixModel[];
    form: RowDrawerForm;
    errors: RowDrawerFieldErrors;
    /** The variation axes of the category the form shows, with their values. */
    axes: RowDrawerAxis[];
    categoryOptions: NameOption[];
    brandOptions: NameOption[];
    supplierOptions: NameOption[];
};

export type UseRowDrawerResult = {
    isOpen: boolean;
    /**
     * The open row, or the last one while the drawer closes (so its content
     * stays through the exit); `null` before any row was opened.
     */
    model: RowDrawerModel | null;
    catalogStatus: RowDrawerCatalogStatus;
    onRetryCatalog: () => void;
    /** Save waits while a number field holds something that is not a number. */
    canSave: boolean;
    onFieldChange: <Field extends RowDrawerField>(
        field: Field,
        value: RowDrawerForm[Field],
    ) => void;
    onAxisChange: (attributeTypeId: string, key: string | null) => void;
    /** Writes the edits, closes the drawer and asks for a check of every row. */
    onSave: () => void;
    /**
     * Asks to close without writing: Cancel, the close button, Escape, the
     * backdrop. With unsaved edits it asks first (`isDiscarding`).
     */
    onClose: () => void;
    /** Closing would lose unsaved edits: the discard question is open. */
    isDiscarding: boolean;
    /** The question's Keep editing: the drawer stays, edits and all. */
    onKeepEditing: () => void;
    /** The question's Discard: closes and drops the edits. */
    onDiscard: () => void;
    /** Exclude row / Include row: the step's own, so an include is checked again. */
    onToggleExclusion: () => void;
    /** Makes the row an explicit restock of a candidate and checks every row again; the drawer stays. */
    onPickCandidate: (variantId: string) => void;
    /** Takes the pick back and checks every row again; the drawer stays. */
    onClearPick: () => void;
    /**
     * Gives every row of the group that row's values for the fields that
     * differ (a bulk action: a count toast with Undo) and checks every row
     * again; the drawer stays.
     */
    onTakeGroupValues: (rowNumber: number) => void;
    /** Picks one of a figure's values; nothing is written yet. */
    onPickVariantValue: (
        field: VariantMismatchField,
        value: number | null,
    ) => void;
    /**
     * Gives the picked value of a figure to every row of the variant (a
     * bulk action: a count toast with Undo) and checks every row again; the
     * drawer stays.
     */
    onApplyVariantValue: (field: VariantMismatchField) => void;
    /**
     * Applies a barcode fix to the row's group alone (a bulk action: a count
     * toast with Undo) and checks every row again; the drawer stays.
     */
    onApplyBarcodeFix: (code: RowDrawerBarcodeFixCode) => void;
    /**
     * Makes the barcode of a row of the variant its main one
     * (`primaryBarcode`) and checks every row again; the drawer stays.
     */
    onMakeMainBarcode: (rowNumber: number) => void;
};

const EMPTY_ATTRIBUTE_TYPES: readonly AttributeType[] = [];
const NO_PICKS: RowDrawerVariantPicks = {};

/** The form fields a barcode fix writes, so the form shows the new values. */
const BARCODE_FIX_FIELDS = {
    BARCODE_LOOKS_LIKE_IMEI: ['barcode', 'lotNumber'],
    BARCODE_SHARED_ACROSS_SKUS: ['barcode'],
} as const satisfies Record<RowDrawerBarcodeFixCode, readonly RowDrawerField[]>;

/** Edits without some fields', so the form shows what the line now holds. */
const withoutFieldEdits = (
    edits: RowDrawerEdits,
    fields: readonly RowDrawerField[],
): RowDrawerEdits => {
    const kept = { ...edits.fields };
    fields.forEach((field) => delete kept[field]);
    return { ...edits, fields: kept };
};

/**
 * The row drawer's logic. The row is in the address (`?row=`, written by the
 * step's `onOpenRow`); a row the store does not hold opens nothing.
 *
 * - **The form** is the row as it is (`initialFormOf`, read through the last
 *   check) with the user's edits on top (`formOf`). Only edits are kept, so a
 *   new check under an open drawer refreshes its problems and leaves what the
 *   user typed.
 * - **Save** writes only what changed (`patchesOf`): the product's fields to
 *   every row of the group (`updateGroup`), the variant's and the batch's to
 *   this row (`updateLine`). Then it asks the step for a check and closes, so
 *   the table shows the check running.
 * - **Edits start empty each time a row opens**, and are kept while the drawer
 *   closes, so its content does not jump back during the exit. The reset runs
 *   during render (React's "adjust state when a prop changes"), not in an
 *   effect: reopening the same row must not flash its last edits.
 * - **Closing with unsaved edits asks first** (`hasUnsavedEdits`): Cancel,
 *   the close button, Escape and the backdrop all come to `onClose`, which
 *   opens the discard question instead of closing. Keep editing leaves the
 *   drawer and its edits as they were; Discard closes. Save writes, so it
 *   closes without asking. Another row cannot be opened while the drawer
 *   is (it is modal), so a switch never meets unsaved edits.
 * - **The catalog** (categories, brands, suppliers, the attribute dictionary)
 *   is read once a row is opened; the Names step has usually read it already.
 * - **A candidate pick, Clear pick and Use for all rows** write the store at
 *   once and ask for a check, as Exclude does, and the drawer stays: its
 *   notes follow the check, and the edits stay edits for Save. A pick writes
 *   the row's `variantId`; its candidate is remembered for the card
 *   ("Restocks …"), as the next check sends no candidates for a picked row.
 *   Use for all rows writes the chosen row's cells for the differing fields
 *   to every row of the group and drops the edits of those fields, so the
 *   form shows the taken values.
 * - **The group fixes** (bos-app#219, US-07). A `GROUP_FIELDS_DIFFER` row
 *   marks the row the bulk fix would take every field from (Suggested). A
 *   `VARIANT_FIELDS_DIFFER` row lists each differing figure's values with
 *   their rows, the bulk fix's default picked; a pick is kept while the
 *   row is shown, and applying it gives the value to the variant's rows
 *   (`params.rows`), not the whole SKU. Use for all rows and the figure's
 *   apply are the store's `applyGroupPick`, a bulk action, so the step
 *   toasts the count with Undo (`onGroupFixApplied`). A row whose new
 *   variant keeps several barcodes (`BARCODES_KEPT_AS_ALTERNATES`) lists
 *   them; Make main flags an extra's row (`makeMainBarcode`), toasts, and
 *   asks for a check, which names the new main barcode. An IMEI typed in
 *   Barcode (`BARCODE_LOOKS_LIKE_IMEI`) offers Move to Lot number, and a
 *   barcode on several SKUs (`BARCODE_SHARED_ACROSS_SKUS`) offers to keep
 *   it on the first SKU by row: the bulk fix's default for that group
 *   alone, through `applyGroupPick`, so it toasts with Undo too
 *   (bos-app#228).
 */
export const useRowDrawer = ({
    onExcludeRow,
    onIncludeRow,
    onRecheck,
    onGroupFixApplied,
}: UseRowDrawerOptions): UseRowDrawerResult => {
    const { state, actions } = useCatalogImport();
    const [searchParams, setSearchParams] = useSearchParams();

    const requested = readDrawerRow(searchParams);
    const lineOf = React.useCallback(
        (rowNumber: number | null) =>
            rowNumber === null
                ? undefined
                : state.lines.find((line) => line.rowNumber === rowNumber),
        [state.lines],
    );
    const resultOf = React.useCallback(
        (rowNumber: number | null) =>
            rowNumber === null
                ? undefined
                : state.preview?.lines.find(
                      (line) => line.rowNumber === rowNumber,
                  ),
        [state.preview],
    );
    const openRow =
        requested !== null && lineOf(requested) && resultOf(requested)
            ? requested
            : null;

    const [openKey, setOpenKey] = React.useState<number | null>(openRow);
    const [shown, setShown] = React.useState<{
        rowNumber: number | null;
        edits: RowDrawerEdits;
        picks: RowDrawerVariantPicks;
    }>({ rowNumber: openRow, edits: NO_EDITS, picks: {} });
    const [isDiscarding, setIsDiscarding] = React.useState(false);
    if (openRow !== openKey) {
        setOpenKey(openRow);
        setIsDiscarding(false);
        if (openRow !== null) {
            setShown({ rowNumber: openRow, edits: NO_EDITS, picks: {} });
        }
    }
    // The candidate each row was given by a pick; kept while the step lives.
    const [picked, setPicked] = React.useState<
        ReadonlyMap<number, RowDrawerCandidate>
    >(() => new Map());
    const rowNumber = openRow ?? shown.rowNumber;
    const edits = shown.rowNumber === rowNumber ? shown.edits : NO_EDITS;
    const picks = shown.rowNumber === rowNumber ? shown.picks : NO_PICKS;

    const isActive = rowNumber !== null;
    const brandsQuery = useAllBrandsQuery(isActive);
    const categoriesQuery = useCategoryPickerQuery(isActive);
    const suppliersQuery = useSupplierPickerQuery(isActive);
    const attributeTypesQuery = useAttributeTypesQuery(isActive);

    const attributeTypes = attributeTypesQuery.data?.success
        ? attributeTypesQuery.data.response.items
        : EMPTY_ATTRIBUTE_TYPES;
    const context = React.useMemo<RowDrawerContext>(
        () => ({
            attributeTypes,
            names: state.preview?.names ?? null,
            decisions: state.decisions,
        }),
        [attributeTypes, state.decisions, state.preview],
    );

    const catalogStatus: RowDrawerCatalogStatus =
        brandsQuery.isError ||
        categoriesQuery.isError ||
        suppliersQuery.isError ||
        attributeTypesQuery.isError ||
        categoriesQuery.data?.success === false ||
        suppliersQuery.data?.success === false ||
        attributeTypesQuery.data?.success === false
            ? 'error'
            : brandsQuery.data &&
                categoriesQuery.data?.success &&
                suppliersQuery.data?.success &&
                attributeTypesQuery.data?.success
              ? 'ready'
              : 'loading';

    const entityOptions = React.useMemo(
        () => ({
            categories: categoriesQuery.data?.success
                ? categoryOptionsOf(categoriesQuery.data.response.items)
                : [],
            brands: brandsQuery.data ? namedOptionsOf(brandsQuery.data) : [],
            suppliers: suppliersQuery.data?.success
                ? namedOptionsOf(suppliersQuery.data.response.items)
                : [],
        }),
        [brandsQuery.data, categoriesQuery.data, suppliersQuery.data],
    );
    const newValueTexts = React.useMemo(
        () => newValueTextsOf(state.preview?.lines ?? []),
        [state.preview],
    );

    const line = lineOf(rowNumber);
    const result = resultOf(rowNumber);
    const isExcluded = rowNumber !== null && state.exclusions.has(rowNumber);
    const groupRows = React.useMemo(
        () =>
            rowNumber === null
                ? []
                : [rowNumber, ...groupSiblings(state, rowNumber)].sort(
                      (a, b) => a - b,
                  ),
        [rowNumber, state],
    );

    const groupLines = React.useMemo(
        () =>
            state.lines.filter((candidate) =>
                groupRows.includes(candidate.rowNumber),
            ),
        [groupRows, state.lines],
    );

    const model = React.useMemo<RowDrawerModel | null>(() => {
        if (rowNumber === null || !line || !result) {
            return null;
        }
        const form = formOf(line, result, context, edits);
        const names = context.names;
        const candidates = candidatesOf(line, result);
        const pickedId = pickedVariantIdOf(line);
        const remembered = picked.get(rowNumber);
        return {
            rowNumber,
            status: statusOf(result, isExcluded),
            isExcluded,
            sku: line.sku?.trim() ?? '',
            groupRows,
            notes: notesOf(result),
            candidates: pickedId === null ? candidates : [],
            pick:
                pickedId === null
                    ? null
                    : {
                          variantId: pickedId,
                          candidate:
                              remembered?.variantId === pickedId
                                  ? remembered
                                  : (candidates.find(
                                        (candidate) =>
                                            candidate.variantId === pickedId,
                                    ) ?? null),
                      },
            groupMismatch: groupMismatchOf(
                rowNumber,
                result,
                groupLines,
                context,
                entityOptions,
                groupFieldsSuggestedRow(state, rowNumber),
            ),
            variantFix: variantFixOf(state, rowNumber, result, picks),
            barcodes: barcodesOf(result, state.lines),
            barcodeFixes: barcodeFixesOf(state, rowNumber, result),
            form,
            errors: fieldErrorsOf(form),
            axes: categoryAxesOf(form.categoryKey, context).map((type) => ({
                attributeTypeId: type.id,
                name: type.name,
                options: axisOptionsOf(
                    type,
                    newValueTexts,
                    form.axisValues[type.id] ?? null,
                ),
            })),
            categoryOptions: pickerOptionsOf(
                entityOptions.categories,
                names?.categories ?? [],
                form.categoryKey,
            ),
            brandOptions: pickerOptionsOf(
                entityOptions.brands,
                names?.brands ?? [],
                form.brandKey,
            ),
            supplierOptions: pickerOptionsOf(
                entityOptions.suppliers,
                names?.suppliers ?? [],
                form.supplierKey,
            ),
        };
    }, [
        context,
        edits,
        entityOptions,
        groupLines,
        groupRows,
        isExcluded,
        line,
        newValueTexts,
        picked,
        picks,
        result,
        rowNumber,
        state,
    ]);

    const onFieldChange = React.useCallback(
        <Field extends RowDrawerField>(
            field: Field,
            value: RowDrawerForm[Field],
        ) => {
            setShown((current) => ({
                ...current,
                edits: {
                    ...current.edits,
                    fields: { ...current.edits.fields, [field]: value },
                },
            }));
        },
        [],
    );
    const onAxisChange = React.useCallback(
        (attributeTypeId: string, key: string | null) => {
            setShown((current) => ({
                ...current,
                edits: {
                    ...current.edits,
                    axisValues: {
                        ...current.edits.axisValues,
                        [attributeTypeId]: key,
                    },
                },
            }));
        },
        [],
    );

    const closeDrawer = React.useCallback(() => {
        setIsDiscarding(false);
        setSearchParams((current) => writeDrawerRow(current, null), {
            replace: true,
        });
    }, [setSearchParams]);

    const isDirty = React.useMemo(
        () =>
            openRow !== null &&
            line !== undefined &&
            result !== undefined &&
            hasUnsavedEdits(line, result, context, edits),
        [context, edits, line, openRow, result],
    );

    const onClose = React.useCallback(() => {
        if (isDirty) {
            setIsDiscarding(true);
            return;
        }
        closeDrawer();
    }, [closeDrawer, isDirty]);

    const onKeepEditing = React.useCallback(() => setIsDiscarding(false), []);

    const canSave =
        openRow !== null &&
        model !== null &&
        Object.keys(model.errors).length === 0;

    const onSave = React.useCallback(() => {
        if (openRow === null || !line || !result || !canSave) {
            return;
        }
        const patches = patchesOf(line, result, context, edits);
        if (!isEmptyPatch(patches.group)) {
            actions.updateGroup(groupRows, patches.group);
        }
        if (!isEmptyPatch(patches.line)) {
            actions.updateLine(openRow, patches.line);
        }
        onRecheck();
        closeDrawer();
    }, [
        actions,
        canSave,
        closeDrawer,
        context,
        edits,
        groupRows,
        line,
        onRecheck,
        openRow,
        result,
    ]);

    const onToggleExclusion = React.useCallback(() => {
        if (openRow === null) {
            return;
        }
        if (isExcluded) {
            onIncludeRow(openRow);
        } else {
            onExcludeRow(openRow);
        }
    }, [isExcluded, onExcludeRow, onIncludeRow, openRow]);

    const onPickCandidate = React.useCallback(
        (variantId: string) => {
            const candidate = model?.candidates.find(
                (option) => option.variantId === variantId,
            );
            if (openRow === null || !candidate) {
                return;
            }
            setPicked((current) => new Map(current).set(openRow, candidate));
            actions.updateLine(openRow, { variantId });
            onRecheck();
        },
        [actions, model, onRecheck, openRow],
    );

    const onClearPick = React.useCallback(() => {
        if (openRow === null || !model?.pick) {
            return;
        }
        setPicked((current) => {
            const next = new Map(current);
            next.delete(openRow);
            return next;
        });
        actions.updateLine(openRow, { variantId: null });
        onRecheck();
    }, [actions, model, onRecheck, openRow]);

    /** A group pick applied: the picked fields show the new values, the step toasts. */
    const afterGroupPick = React.useCallback(
        (changed: number, fields: readonly RowDrawerField[]) => {
            if (changed > 0) {
                setShown((current) => ({
                    ...current,
                    edits: withoutFieldEdits(current.edits, fields),
                }));
                onRecheck();
            }
            onGroupFixApplied(changed);
        },
        [onGroupFixApplied, onRecheck],
    );

    const onTakeGroupValues = React.useCallback(
        (sourceRow: number) => {
            const mismatch = model?.groupMismatch;
            if (
                openRow === null ||
                !mismatch ||
                !mismatch.rows.some((row) => row.rowNumber === sourceRow)
            ) {
                return;
            }
            const changed = actions.applyGroupPick(openRow, {
                code: 'GROUP_FIELDS_DIFFER',
                sourceRow,
            });
            afterGroupPick(
                changed,
                mismatch.fields.map((field) => FORM_FIELD_OF[field]),
            );
        },
        [actions, afterGroupPick, model, openRow],
    );

    const onPickVariantValue = React.useCallback(
        (field: VariantMismatchField, value: number | null) => {
            setShown((current) => ({
                ...current,
                picks: { ...current.picks, [field]: value },
            }));
        },
        [],
    );

    const onApplyVariantValue = React.useCallback(
        (field: VariantMismatchField) => {
            const figure = model?.variantFix?.figures.find(
                (candidate) => candidate.field === field,
            );
            if (openRow === null || !figure) {
                return;
            }
            const changed = actions.applyGroupPick(openRow, {
                code: 'VARIANT_FIELDS_DIFFER',
                field,
                value: figure.picked,
            });
            afterGroupPick(changed, [field]);
        },
        [actions, afterGroupPick, model, openRow],
    );

    const onApplyBarcodeFix = React.useCallback(
        (code: RowDrawerBarcodeFixCode) => {
            if (
                openRow === null ||
                !model?.barcodeFixes.some((fix) => fix.code === code)
            ) {
                return;
            }
            const changed = actions.applyGroupPick(openRow, { code });
            afterGroupPick(changed, BARCODE_FIX_FIELDS[code]);
        },
        [actions, afterGroupPick, model, openRow],
    );

    const onMakeMainBarcode = React.useCallback(
        (sourceRow: number) => {
            const extra = model?.barcodes?.extras.find(
                (candidate) => candidate.rowNumber === sourceRow,
            );
            if (openRow === null || !extra) {
                return;
            }
            if (actions.makeMainBarcode(sourceRow) === 0) {
                return;
            }
            onRecheck();
            showLocalizedToast('success', {
                key: 'catalogImport.products.bulk.madeMain',
                params: { barcode: extra.barcode },
            });
        },
        [actions, model, onRecheck, openRow],
    );

    const { refetch: refetchBrands } = brandsQuery;
    const { refetch: refetchCategories } = categoriesQuery;
    const { refetch: refetchSuppliers } = suppliersQuery;
    const { refetch: refetchAttributeTypes } = attributeTypesQuery;
    const onRetryCatalog = React.useCallback(() => {
        void refetchBrands();
        void refetchCategories();
        void refetchSuppliers();
        void refetchAttributeTypes();
    }, [
        refetchAttributeTypes,
        refetchBrands,
        refetchCategories,
        refetchSuppliers,
    ]);

    return {
        isOpen: openRow !== null,
        model,
        catalogStatus,
        onRetryCatalog,
        canSave,
        onFieldChange,
        onAxisChange,
        onSave,
        onClose,
        isDiscarding,
        onKeepEditing,
        onDiscard: closeDrawer,
        onToggleExclusion,
        onPickCandidate,
        onClearPick,
        onTakeGroupValues,
        onPickVariantValue,
        onApplyVariantValue,
        onApplyBarcodeFix,
        onMakeMainBarcode,
    };
};
